import type { CookieConsentConfig } from 'vanilla-cookieconsent';

export const cookieConsentConfig: CookieConsentConfig = {
  guiOptions: {
    consentModal: {
      layout: 'box',
      position: 'bottom right',
      equalWeightButtons: true,
      flipButtons: false
    },
    preferencesModal: {
      layout: 'box',
      position: 'right',
      equalWeightButtons: true,
      flipButtons: false
    }
  },

  categories: {
    necessary: {
      readOnly: true,
      enabled: true
    },
    analytics: {
      enabled: false
    }
  },

  language: {
    default: 'de',
    autoDetect: 'browser',
    translations: {
      de: {
        consentModal: {
          title: 'Wir verwenden Cookies',
          description:
            'Wir nutzen Cookies auf unserer Website, um grundlegende Funktionen zu gewährleisten, Inhalte optimal darzustellen und anonyme Zugriffsstatistiken zu erheben. Sie können selbst entscheiden, welche Kategorien Sie zulassen möchten.',
          acceptAllBtn: 'Alle akzeptieren',
          acceptNecessaryBtn: 'Nur notwendige',
          showPreferencesBtn: 'Einstellungen verwalten',
          footer: '<a href="/datenschutz">Datenschutzerklärung</a><a href="/impressum">Impressum</a>'
        },
        preferencesModal: {
          title: 'Cookie-Präferenzen verwalten',
          acceptAllBtn: 'Alle akzeptieren',
          acceptNecessaryBtn: 'Nur notwendige',
          savePreferencesBtn: 'Einstellungen speichern',
          closeIconLabel: 'Schließen',
          serviceCounterLabel: 'Dienste',
          sections: [
            {
              title: 'Ihre Privatsphäre ist uns wichtig',
              description:
                'Hier können Sie festlegen, welche Cookies Sie zulassen möchten. Ihre Auswahl können Sie jederzeit über den Link „Cookie-Einstellungen“ im Fußbereich unserer Website ändern.'
            },
            {
              title: 'Technisch notwendige Cookies',
              description:
                'Diese Cookies sind für den fehlerfreien Betrieb der Website unerlässlich (z. B. Speicherung Ihrer Cookie- und Theme-Präferenzen) und können nicht deaktiviert werden.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analyse & Performance',
              description:
                'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, um Navigation und Inhalte stetig zu verbessern. Die Daten werden ausschließlich anonymisiert erfasst.',
              linkedCategory: 'analytics'
            },
            {
              title: 'Weitere Informationen',
              description:
                'Ausführliche Hinweise zur Datenverarbeitung sowie zu Ihren Rechten finden Sie in unserer <a href="/datenschutz">Datenschutzerklärung</a> sowie in unserem <a href="/impressum">Impressum</a>.'
            }
          ]
        }
      },

      en: {
        consentModal: {
          title: 'We use cookies',
          description:
            'We use cookies to ensure basic site functionality, display content optimally, and collect anonymous traffic statistics. You can decide which categories you wish to allow.',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Necessary only',
          showPreferencesBtn: 'Manage preferences',
          footer: '<a href="/datenschutz">Privacy Policy</a><a href="/impressum">Legal Notice</a>'
        },
        preferencesModal: {
          title: 'Manage cookie preferences',
          acceptAllBtn: 'Accept all',
          acceptNecessaryBtn: 'Necessary only',
          savePreferencesBtn: 'Save preferences',
          closeIconLabel: 'Close',
          serviceCounterLabel: 'Services',
          sections: [
            {
              title: 'Your privacy is important to us',
              description:
                'You can select which cookie categories you wish to enable. You may review and update your choices at any time via the "Cookie Settings" link in our footer.'
            },
            {
              title: 'Strictly Necessary Cookies',
              description:
                'These cookies are essential for website operation (e.g., remembering your theme and cookie consent preferences) and cannot be disabled.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analytics & Performance',
              description:
                'These cookies allow us to evaluate visitor interactions anonymously to help us improve site navigation and performance.',
              linkedCategory: 'analytics'
            },
            {
              title: 'More Information',
              description:
                'For further information on data processing and your rights, please see our <a href="/datenschutz">Privacy Policy</a> and <a href="/impressum">Legal Notice</a>.'
            }
          ]
        }
      },

      tr: {
        consentModal: {
          title: 'Çerezleri kullanıyoruz',
          description:
            'Web sitemizin temel işlevlerini sağlamak, içeriği en iyi şekilde sunmak ve anonim ziyaretçi istatistikleri toplamak için çerezleri kullanıyoruz. Hangi kategorilere izin vermek istediğinizi seçebilirsiniz.',
          acceptAllBtn: 'Tümünü kabul et',
          acceptNecessaryBtn: 'Yalnızca zorunlu',
          showPreferencesBtn: 'Tercihleri yönet',
          footer: '<a href="/datenschutz">Gizlilik Politikası</a><a href="/impressum">Künye</a>'
        },
        preferencesModal: {
          title: 'Çerez tercihlerini yönetin',
          acceptAllBtn: 'Tümünü kabul et',
          acceptNecessaryBtn: 'Yalnızca zorunlu',
          savePreferencesBtn: 'Ayarları kaydet',
          closeIconLabel: 'Kapat',
          serviceCounterLabel: 'Hizmetler',
          sections: [
            {
              title: 'Gizliliğiniz bizim için önemlidir',
              description:
                'Buradan izin vermek istediğiniz çerez kategorilerini belirleyebilirsiniz. Tercihlerinizi sayfanın altındaki „Çerez Ayarları“ bağlantısından dilediğiniz zaman güncelleyebilirsiniz.'
            },
            {
              title: 'Zorunlu Çerezler',
              description:
                'Bu çerezler web sitesinin güvenli ve düzgün çalışması için gereklidir ve devre dışı bırakılamaz.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analiz ve Performans',
              description:
                'Bu çerezler ziyaretçilerin sitemizle nasıl etkileşimde bulunduğunu anonim olarak anlamamıza ve sitemizi geliştirmemize yardımcı olur.',
              linkedCategory: 'analytics'
            },
            {
              title: 'Daha Fazla Bilgi',
              description:
                'Kişisel verilerinizin işlenmesi ve haklarınız hakkında detaylı bilgi için <a href="/datenschutz">Gizlilik Politikamızı</a> inceleyebilirsiniz.'
            }
          ]
        }
      },

      ru: {
        consentModal: {
          title: 'Мы используем файлы cookie',
          description:
            'Мы используем файлы cookie для обеспечения базовых функций сайта, оптимального отображения контента и сбора анонимной статистики. Вы можете настроить свои предпочтения в любое время.',
          acceptAllBtn: 'Принять все',
          acceptNecessaryBtn: 'Только необходимые',
          showPreferencesBtn: 'Управление настройками',
          footer: '<a href="/datenschutz">Политика конфиденциальности</a><a href="/impressum">Выходные данные</a>'
        },
        preferencesModal: {
          title: 'Настройки файлов cookie',
          acceptAllBtn: 'Принять все',
          acceptNecessaryBtn: 'Только необходимые',
          savePreferencesBtn: 'Сохранить настройки',
          closeIconLabel: 'Закрыть',
          serviceCounterLabel: 'Сервисы',
          sections: [
            {
              title: 'Ваша конфиденциальность важна для нас',
              description:
                'Здесь вы можете выбрать, какие категории файлов cookie вы разрешаете использовать. Вы всегда можете изменить свои настройки по ссылке внизу страницы.'
            },
            {
              title: 'Обязательные файлы cookie',
              description:
                'Эти файлы cookie необходимы для правильной работы сайта и не могут быть отключены.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Аналитика и производительность',
              description:
                'Эти файлы cookie помогают нам анонимно собирать данные об использовании сайта для улучшения контента и навигации.',
              linkedCategory: 'analytics'
            },
            {
              title: 'Дополнительная информация',
              description:
                'Подробную информацию о защите данных и ваших правах можно найти в нашей <a href="/datenschutz">Политике конфиденциальности</a>.'
            }
          ]
        }
      },

      hr: {
        consentModal: {
          title: 'Koristimo kolačiće',
          description:
            'Kolačiće koristimo kako bismo osigurali osnovne funkcionalnosti stranice, optimalan prikaz sadržaja i prikupljanje anonimne statistike. Možete odabrati koje kategorije želite dopustiti.',
          acceptAllBtn: 'Prihvati sve',
          acceptNecessaryBtn: 'Samo nužni',
          showPreferencesBtn: 'Upravljanje postavkama',
          footer: '<a href="/datenschutz">Pravila privatnosti</a><a href="/impressum">Impresum</a>'
        },
        preferencesModal: {
          title: 'Upravljanje postavkama kolačića',
          acceptAllBtn: 'Prihvati sve',
          acceptNecessaryBtn: 'Samo nužni',
          savePreferencesBtn: 'Spremi postavke',
          closeIconLabel: 'Zatvori',
          serviceCounterLabel: 'Usluge',
          sections: [
            {
              title: 'Vaša privatnost nam je važna',
              description:
                'Ovdje možete odabrati koje kolačiće želite dopustiti. Postavke možete bilo kada promijeniti putem poveznice na dnu stranice.'
            },
            {
              title: 'Nužni kolačići',
              description:
                'Ovi kolačići su neophodni za ispravan rad web stranice i ne mogu se isključiti.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analitika i performanse',
              description:
                'Ovi kolačići omogućuju nam anonimno mjerenje posjećenosti kako bismo poboljšali korisničko iskustvo.',
              linkedCategory: 'analytics'
            },
            {
              title: 'Dodatne informacije',
              description:
                'Za detaljne informacije posjetite naša <a href="/datenschutz">Pravila privatnosti</a> i <a href="/impressum">Impresum</a>.'
            }
          ]
        }
      },

      it: {
        consentModal: {
          title: 'Utilizziamo i cookie',
          description:
            'Utilizziamo i cookie per garantire il funzionamento del sito web, ottimizzare la visualizzazione dei contenuti e raccogliere statistiche anonime sul traffico. Puoi decidere quali categorie autorizzare.',
          acceptAllBtn: 'Accetta tutti',
          acceptNecessaryBtn: 'Solo necessari',
          showPreferencesBtn: 'Gestisci preferenze',
          footer: '<a href="/datenschutz">Informativa sulla privacy</a><a href="/impressum">Note legali</a>'
        },
        preferencesModal: {
          title: 'Gestisci le preferenze dei cookie',
          acceptAllBtn: 'Accetta tutti',
          acceptNecessaryBtn: 'Solo necessari',
          savePreferencesBtn: 'Salva preferenze',
          closeIconLabel: 'Chiudi',
          serviceCounterLabel: 'Servizi',
          sections: [
            {
              title: 'La tua privacy è importante per noi',
              description:
                'Qui puoi selezionare le categorie di cookie che desideri consentire. Puoi modificare la tua scelta in qualsiasi momento tramite il link nel footer.'
            },
            {
              title: 'Cookie strettamente necessari',
              description:
                'Questi cookie sono indispensabili per il corretto funzionamento del sito e non possono essere disattivati.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analisi e prestazioni',
              description:
                'Questi cookie ci consentono di analizzare in modo anonimo le visite per migliorare continuamente i nostri contenuti e servizi.',
              linkedCategory: 'analytics'
            },
            {
              title: 'Ulteriori informazioni',
              description:
                'Per maggiori informazioni consulta la nostra <a href="/datenschutz">Informativa sulla privacy</a> e le <a href="/impressum">Note legali</a>.'
            }
          ]
        }
      }
    }
  }
};
